import type { ReactNode } from 'react';
import { Component } from 'react';

import { getAppInsights } from 'analytics/appInsights';

import { ErrorCodes, ErrorPage } from './state/errors/ErrorPage';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * ErrorBoundary component that catches React errors and displays a fallback UI.
 * Logs errors to AppInsights for monitoring.
 *
 * This prevents the entire app from crashing when a component throws an error.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log to AppInsights
    const appInsights = getAppInsights();
    appInsights.trackException({
      exception: error,
      properties: {
        componentStack: errorInfo.componentStack
      }
    });

    // Call optional error handler
    this.props.onError?.(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || <ErrorPage code={ErrorCodes.Generic} />;
    }

    return this.props.children;
  }
}
